import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { analyzeTrends } from '../utils/openai';

const formatNumber = (num) => {
  return num.toLocaleString();
};

const AITrends = ({ reviews }) => {
  const [trendData, setTrendData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSegments, setSelectedSegments] = useState({
    'General Hotel': true,
    'Ski Mountain': true,
    'Aquatopia': true,
    'Camelbeach': true,
    'Mountain Adventures': true
  });

  const segments = [
    { name: 'General Hotel', color: '#2563eb' },
    { name: 'Ski Mountain', color: '#dc2626' },
    { name: 'Aquatopia', color: '#0891b2' },
    { name: 'Camelbeach', color: '#ea580c' },
    { name: 'Mountain Adventures', color: '#16a34a' }
  ];

  const generateTrends = async () => {
    if (reviews.length === 0) {
      setError('No reviews available for trend analysis');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await analyzeTrends(reviews);
      setTrendData(result.chartData);
      setAnalysis(result.analysis);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Remove automatic loading - let user trigger it manually

  const toggleSegment = (segmentName) => {
    setSelectedSegments(prev => ({
      ...prev,
      [segmentName]: !prev[segmentName]
    }));
  };

  const formatTooltip = (value, name) => {
    if (name && value) {
      return [`${value}/5`, name];
    }
    return null;
  };

  const getTrendIcon = (trend) => {
    switch (trend?.toLowerCase()) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      case 'stable': return '➡️';
      default: return '📊';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend?.toLowerCase()) {
      case 'improving': return '#10b981';
      case 'declining': return '#ef4444';
      case 'stable': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="ai-trends">
        <div className="trends-header">
          <h2>📊 Trend Analysis</h2>
        </div>
        <div className="loading-trends">
          <div className="loading-spinner"></div>
          <p>Analyzing trends with AI...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-trends">
        <div className="trends-header">
          <h2>📊 Trend Analysis</h2>
          <button className="refresh-btn" onClick={generateTrends}>
            🔄 Retry
          </button>
        </div>
        <div className="error-message">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  if (!trendData || !analysis) {
    return (
      <div className="ai-trends">
        <div className="trends-header">
          <h2>📊 Trend Analysis</h2>
          <button className="generate-btn" onClick={generateTrends}>
            Generate Trends
          </button>
        </div>
        <p>Click "Generate Trends" to analyze rating patterns over time.</p>
      </div>
    );
  }

  return (
    <div className="ai-trends">
      <div className="trends-header">
        <div>
          <h2>📊 Trend Analysis</h2>
          <p className="data-scope">Analyzing {formatNumber(reviews.length)} reviews</p>
          {reviews.length < 100 && (
            <p className="filter-notice">🔍 Filtered data - adjust filters above to change scope</p>
          )}
        </div>
        <button className="refresh-btn" onClick={generateTrends}>
          🔄 Refresh
        </button>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <div className="chart-controls">
          <h3>Rating Trends Over Time</h3>
          <div className="segment-toggles">
            {segments.map(segment => (
              <label key={segment.name} className="segment-toggle">
                <input
                  type="checkbox"
                  checked={selectedSegments[segment.name]}
                  onChange={() => toggleSegment(segment.name)}
                />
                <span 
                  className="toggle-indicator"
                  style={{ backgroundColor: segment.color }}
                ></span>
                {segment.name}
              </label>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              domain={[1, 5]}
              tick={{ fontSize: 12 }}
              label={{ value: 'Average Rating', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={formatTooltip}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend />
            
            {segments.map(segment => (
              selectedSegments[segment.name] && (
                <Line
                  key={segment.name}
                  type="monotone"
                  dataKey={segment.name}
                  stroke={segment.color}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls={false}
                />
              )
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* AI Analysis */}
      <div className="analysis-container">
        <div className="analysis-grid">
          {/* Trend Analysis */}
          <div className="analysis-card">
            <h3>📈 Trend Analysis</h3>
            <div className="trend-items">
              {analysis.trendAnalysis?.map((trend, index) => (
                <div key={index} className="trend-item">
                  <div className="trend-header">
                    <span className="trend-icon">{getTrendIcon(trend.trend)}</span>
                    <span className="trend-segment">{trend.segment}</span>
                    <span 
                      className="trend-status"
                      style={{ color: getTrendColor(trend.trend) }}
                    >
                      {trend.trend}
                    </span>
                  </div>
                  <div className="trend-details">
                    <div className="trend-change">{trend.change}</div>
                    <div className="trend-insight">{trend.insight}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Seasonal Patterns */}
          <div className="analysis-card">
            <h3>📅 Seasonal Patterns</h3>
            <div className="pattern-items">
              {analysis.seasonalPatterns?.map((pattern, index) => (
                <div key={index} className="pattern-item">
                  <div className="pattern-description">{pattern.pattern}</div>
                  <div className="pattern-segments">
                    <strong>Affects:</strong> {pattern.segments?.join(', ')}
                  </div>
                  <div className="pattern-recommendation">
                    <strong>Recommendation:</strong> {pattern.recommendation}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Emerging Issues */}
          <div className="analysis-card">
            <h3>⚠️ Emerging Issues</h3>
            <div className="issue-items">
              {analysis.emergingIssues?.map((issue, index) => (
                <div key={index} className="issue-item">
                  <div className="issue-header">
                    <span className="issue-text">{issue.issue}</span>
                    <span 
                      className="severity-badge"
                      style={{ backgroundColor: getSeverityColor(issue.severity) }}
                    >
                      {issue.severity?.toUpperCase()}
                    </span>
                  </div>
                  <div className="issue-segments">
                    <strong>Affected areas:</strong> {issue.segments?.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Improvements */}
          <div className="analysis-card">
            <h3>✨ Recent Improvements</h3>
            <div className="improvement-items">
              {analysis.improvements?.map((improvement, index) => (
                <div key={index} className="improvement-item">
                  <div className="improvement-text">{improvement.improvement}</div>
                  <div className="improvement-segments">
                    <strong>In segments:</strong> {improvement.segments?.join(', ')}
                  </div>
                  <div className="improvement-impact">{improvement.impact}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITrends;